/**
 * Subject-Class Assignment Tab
 *
 * Matrix view: rows = subjects, columns = classes grouped by class_group.
 * Local state mirrors server — checkboxes respond instantly.
 * Changes are batched and saved with a single "Save" action.
 * Associations are year-agnostic (no academic year dependency).
 */

import {
  Box, Typography, CircularProgress, Alert, TextField,
  InputAdornment, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Checkbox, Chip,
  Tooltip, Button, Stack, LinearProgress,
  alpha, useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  CheckCircle as CheckCircleIcon,
  MenuBook as SubjectIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSubjectsDirect,
  getClassesDirect,
  getClassSubjectAssociations,
  createClassSubjectAssociation,
  deleteClassSubjectAssociation,
} from '@timetable/api';
import { queryKeys } from '../../lib/queryKeys';
import { useToast } from '../Toast';

interface Props { schoolId: string }

// key: "subjectId|classId"
type AssocSet = Set<string>;

// Group order for display
const GROUP_ORDER = ['Primary', 'Middle', 'Secondary'];

export function SubjectClassAssignmentTab({ schoolId }: Props) {
  const theme = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Local draft state — what the user sees
  const [draft, setDraft] = useState<AssocSet>(new Set());
  // Server state snapshot — used to compute diff on save
  const [serverState, setServerState] = useState<Map<string, string>>(new Map()); // key -> assoc id
  const initialSyncDone = useRef(false);

  const { data: subjects = [], isLoading: loadingSubjects } = useQuery({
    queryKey: queryKeys.subjects.list({ school_id: schoolId }),
    queryFn: () => getSubjectsDirect({ school_id: schoolId }),
    enabled: !!schoolId,
  });

  const { data: classes = [], isLoading: loadingClasses } = useQuery({
    queryKey: queryKeys.classes.list({ school_id: schoolId }),
    queryFn: () => getClassesDirect(schoolId),
    enabled: !!schoolId,
  });

  const { data: associations = [], isLoading: loadingAssoc } = useQuery({
    queryKey: queryKeys.classSubjects.list({ school_id: schoolId }),
    queryFn: () => getClassSubjectAssociations({ school_id: schoolId }),
    enabled: !!schoolId,
  });

  // Sync server → local state on initial load only (not on background refetches)
  useEffect(() => {
    if (loadingAssoc) return;
    const map = new Map<string, string>();
    const set = new Set<string>();
    for (const a of associations) {
      const key = `${a.subject_id}|${a.class_id}`;
      map.set(key, a.id);
      set.add(key);
    }
    setServerState(map);
    // Only reset draft on first load — after that, draft is the source of truth
    if (!initialSyncDone.current) {
      setDraft(new Set(set));
      initialSyncDone.current = true;
    }
  }, [associations, loadingAssoc]);

  // Sort classes numerically within each group, then group them
  const classGroups = useMemo(() => {
    const sorted = [...classes].sort((a, b) =>
      a.class_name.localeCompare(b.class_name, undefined, { numeric: true })
    );

    // Build ordered groups
    const groupMap = new Map<string, typeof sorted>();
    for (const cls of sorted) {
      const group = cls.class_group || 'Other';
      if (!groupMap.has(group)) groupMap.set(group, []);
      groupMap.get(group)!.push(cls);
    }

    // Return in defined order, then any remaining groups alphabetically
    const orderedGroups: Array<{ group: string; classes: typeof sorted }> = [];
    for (const g of GROUP_ORDER) {
      if (groupMap.has(g)) {
        orderedGroups.push({ group: g, classes: groupMap.get(g)! });
        groupMap.delete(g);
      }
    }
    // Append any groups not in GROUP_ORDER
    for (const [group, cls] of groupMap) {
      orderedGroups.push({ group, classes: cls });
    }
    return orderedGroups;
  }, [classes]);

  const sortedClasses = useMemo(
    () => classGroups.flatMap((g) => g.classes),
    [classGroups]
  );

  const filteredSubjects = useMemo(
    () => subjects.filter((s) => s.subject_name.toLowerCase().includes(search.toLowerCase())),
    [subjects, search]
  );

  // Diff: what changed vs server
  const toCreate = useMemo(() => [...draft].filter((k) => !serverState.has(k)), [draft, serverState]);
  const toDelete = useMemo(() => [...serverState.keys()].filter((k) => !draft.has(k)), [draft, serverState]);
  const hasChanges = toCreate.length > 0 || toDelete.length > 0;

  const handleToggle = useCallback((subjectId: string, classId: string) => {
    const key = `${subjectId}|${classId}`;
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleSelectAllForSubject = useCallback((subjectId: string) => {
    const allKeys = sortedClasses.map((c) => `${subjectId}|${c.id}`);
    const allChecked = allKeys.every((k) => draft.has(k));
    setDraft((prev) => {
      const next = new Set(prev);
      if (allChecked) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [sortedClasses, draft]);

  const handleSelectAllForClass = useCallback((classId: string) => {
    const allKeys = filteredSubjects.map((s) => `${s.id}|${classId}`);
    const allChecked = allKeys.length > 0 && allKeys.every((k) => draft.has(k));
    setDraft((prev) => {
      const next = new Set(prev);
      if (allChecked) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [filteredSubjects, draft]);

  const handleSelectAllForGroup = useCallback((groupClasses: Array<{ id: string }>) => {
    const allKeys = filteredSubjects.flatMap((s) => groupClasses.map((c) => `${s.id}|${c.id}`));
    const allChecked = allKeys.length > 0 && allKeys.every((k) => draft.has(k));
    setDraft((prev) => {
      const next = new Set(prev);
      if (allChecked) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return next;
    });
  }, [filteredSubjects, draft]);

  const handleDiscard = () => {
    const set = new Set<string>(serverState.keys());
    setDraft(set);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const creates = toCreate.map((k) => {
        const [subjectId, classId] = k.split('|');
        return createClassSubjectAssociation({ school_id: schoolId, subject_id: subjectId, class_id: classId });
      });
      const deletes = toDelete.map((k) => {
        const id = serverState.get(k)!;
        return deleteClassSubjectAssociation(id);
      });
      await Promise.all([...creates, ...deletes]);

      // Refresh server state from the new draft
      const newMap = new Map(serverState);
      toDelete.forEach((k) => newMap.delete(k));
      setServerState(newMap);

      toast.success(`Saved — ${toCreate.length} added, ${toDelete.length} removed`);

      // Invalidate relevant query caches
      queryClient.invalidateQueries({ queryKey: queryKeys.classSubjects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });

      // Allow next refetch to re-sync draft with real IDs from server
      initialSyncDone.current = false;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save changes');
      handleDiscard(); // roll back draft on error
    } finally {
      setSaving(false);
    }
  };

  const isLoading = loadingSubjects || loadingClasses || loadingAssoc;

  if (!schoolId) return <Alert severity="warning">No school selected.</Alert>;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6">Subject-Class Assignments</Typography>
          <Tooltip title="Tick the classes each subject is taught in. Leave all unchecked to make a subject available to all classes.">
            <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'default' }} />
          </Tooltip>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          {hasChanges && (
            <Chip
              label={`${toCreate.length + toDelete.length} unsaved`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<UndoIcon />}
            onClick={handleDiscard}
            disabled={!hasChanges || saving}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            Save Changes
          </Button>
        </Stack>
      </Stack>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search subjects…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        sx={{ mb: 2, width: 260 }}
      />

      {saving && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto', borderRadius: 2 }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              {/* Group header row */}
              <TableRow>
                <TableCell
                  sx={{
                    minWidth: 220,
                    position: 'sticky',
                    left: 0,
                    zIndex: 4,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                />
                {classGroups.map(({ group, classes: groupClasses }) => {
                  const allGroupKeys = filteredSubjects.flatMap((s) => groupClasses.map((c) => `${s.id}|${c.id}`));
                  const allChecked = allGroupKeys.length > 0 && allGroupKeys.every((k) => draft.has(k));
                  const someChecked = allGroupKeys.some((k) => draft.has(k));
                  return (
                    <TableCell
                      key={group}
                      colSpan={groupClasses.length}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                        borderLeft: `2px solid ${theme.palette.divider}`,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        py: 0.5,
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
                        <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {group}
                        </Typography>
                        <Tooltip title={`${allChecked ? 'Deselect' : 'Select'} all ${group} classes for all subjects`}>
                          <Checkbox
                            checked={allChecked}
                            indeterminate={someChecked && !allChecked}
                            size="small"
                            sx={{ p: 0.25 }}
                            onChange={() => handleSelectAllForGroup(groupClasses)}
                            disabled={saving}
                            inputProps={{ 'aria-label': `Toggle all ${group} classes` }}
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>

              {/* Class column headers row */}
              <TableRow>
                {/* Subject column header */}
                <TableCell
                  sx={{
                    minWidth: 220,
                    fontWeight: 700,
                    position: 'sticky',
                    left: 0,
                    zIndex: 4,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                    borderRight: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Subject
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight={400}>
                    {subjects.length} total
                  </Typography>
                </TableCell>

                {/* Class column headers — click to select all in column */}
                {classGroups.map(({ group, classes: groupClasses }) =>
                  groupClasses.map((cls) => {
                    const colKeys = filteredSubjects.map((s) => `${s.id}|${cls.id}`);
                    const allChecked = colKeys.length > 0 && colKeys.every((k) => draft.has(k));
                    const someChecked = colKeys.some((k) => draft.has(k));
                    return (
                      <TableCell
                        key={cls.id}
                        align="center"
                        sx={{
                          fontWeight: 600,
                          minWidth: 90,
                          cursor: 'pointer',
                          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                          borderLeft: groupClasses[0].id === cls.id ? `2px solid ${theme.palette.divider}` : undefined,
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                          userSelect: 'none',
                        }}
                        onClick={() => handleSelectAllForClass(cls.id)}
                      >
                        <Tooltip title={`Click to ${allChecked ? 'deselect' : 'select'} all for ${cls.class_name}`}>
                          <Box>
                            <SubjectIcon sx={{ fontSize: 14, color: 'text.secondary', display: 'block', mx: 'auto', mb: 0.25 }} />
                            <Typography variant="caption" fontWeight={600} noWrap>{cls.class_name}</Typography>
                            <Checkbox
                              checked={allChecked}
                              indeterminate={someChecked && !allChecked}
                              size="small"
                              sx={{ p: 0, display: 'block', mx: 'auto' }}
                              tabIndex={-1}
                              disableRipple
                              onChange={() => handleSelectAllForClass(cls.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  })
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredSubjects.map((subject) => {
                const assignedCount = sortedClasses.filter((c) => draft.has(`${subject.id}|${c.id}`)).length;
                const serverCount = sortedClasses.filter((c) => serverState.has(`${subject.id}|${c.id}`)).length;
                const rowChanged = assignedCount !== serverCount;
                const allRowChecked = sortedClasses.length > 0 && sortedClasses.every((c) => draft.has(`${subject.id}|${c.id}`));

                return (
                  <TableRow
                    key={subject.id}
                    hover
                    sx={{
                      bgcolor: rowChanged ? alpha(theme.palette.warning.main, 0.04) : undefined,
                    }}
                  >
                    {/* Subject name cell */}
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        bgcolor: rowChanged
                          ? alpha(theme.palette.warning.main, 0.06)
                          : theme.palette.background.paper,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        py: 0.5,
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <Tooltip title={allRowChecked ? 'Deselect all classes' : 'Select all classes'}>
                          <Checkbox
                            checked={allRowChecked}
                            indeterminate={assignedCount > 0 && !allRowChecked}
                            size="small"
                            onChange={() => handleSelectAllForSubject(subject.id)}
                            sx={{ p: 0.25, flexShrink: 0 }}
                            inputProps={{ 'aria-label': `Select all classes for ${subject.subject_name}` }}
                          />
                        </Tooltip>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" alignItems="center" gap={0.75}>
                            <Typography variant="body2" fontWeight={500} noWrap>{subject.subject_name}</Typography>
                            {assignedCount > 0 && (
                              <Typography variant="caption" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                                <CheckCircleIcon sx={{ fontSize: 11 }} />
                                {assignedCount}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Checkbox cells */}
                    {classGroups.map(({ classes: groupClasses }) =>
                      groupClasses.map((cls) => {
                        const key = `${subject.id}|${cls.id}`;
                        const checked = draft.has(key);
                        const wasChecked = serverState.has(key);
                        const cellChanged = checked !== wasChecked;

                        return (
                          <TableCell
                            key={cls.id}
                            align="center"
                            padding="checkbox"
                            sx={{
                              py: 0.25,
                              borderLeft: groupClasses[0].id === cls.id ? `2px solid ${theme.palette.divider}` : undefined,
                              bgcolor: checked
                                ? alpha(theme.palette.primary.main, cellChanged ? 0.12 : 0.06)
                                : cellChanged
                                ? alpha(theme.palette.error.main, 0.06)
                                : undefined,
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <Checkbox
                              checked={checked}
                              onChange={() => handleToggle(subject.id, cls.id)}
                              size="small"
                              color="primary"
                              disabled={saving}
                              inputProps={{ 'aria-label': `Assign ${subject.subject_name} to ${cls.class_name}` }}
                              sx={{
                                transition: 'transform 0.1s',
                                '&:hover': { transform: 'scale(1.15)' },
                              }}
                            />
                          </TableCell>
                        );
                      })
                    )}
                  </TableRow>
                );
              })}

              {filteredSubjects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={sortedClasses.length + 1} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No subjects match "{search}"
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Footer summary */}
      {!isLoading && (
        <Stack direction="row" gap={2} sx={{ mt: 1.5 }} flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            {[...draft].length} total assignments · {subjects.length} subjects · {sortedClasses.length} classes
          </Typography>
          {hasChanges && (
            <Typography variant="caption" color="warning.main">
              {toCreate.length > 0 && `+${toCreate.length} to add`}
              {toCreate.length > 0 && toDelete.length > 0 && ' · '}
              {toDelete.length > 0 && `−${toDelete.length} to remove`}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
